import { Box } from "@mui/material";
import { Drawer } from "@zfp-akademie-a-s/zfp-design";
import { AnalysisClientItemRelationType, type Client } from "Infrastructure/ApiZfp/Schema";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import { useRef } from "react";
import { EditClientForm } from "../Detail/EditClientForm";
import { FamilyMemberForm } from "./FamilyMemberForm";

type Props = {
  readonly clientId?: string;
  readonly clientRelationType?: AnalysisClientItemRelationType;
  readonly onClose: () => void;
  readonly clientGuid?: Client["guid"];
  readonly open: boolean;
};

const getTitle = (
  t: ReturnType<typeof useResource>["t"],
  clientId?: string,
  clientGuid?: string,
  clientRelationType?: AnalysisClientItemRelationType,
): string => {
  if (clientGuid && clientRelationType === AnalysisClientItemRelationType.Client) {
    return t(Resources.AnalysisDetail.Family.MemberDrawer.EditClientTitle);
  }

  if (clientId && clientRelationType === AnalysisClientItemRelationType.RelatedClient) {
    return t(Resources.AnalysisDetail.Family.MemberDrawer.EditFamilyMemberTitle);
  }

  return t(Resources.AnalysisDetail.Family.MemberDrawer.AddFamilyMemberTitle);
};

export const FamilyMemberDrawer: React.FC<Props> = ({ clientId, clientRelationType, clientGuid, onClose, open }) => {
  const { t } = useResource();

  // Freeze the form-selection props while the drawer is animating closed.
  // When the parent resets clientRelationType on close, `open` is already false
  // but the exit animation is still running, causing a flash of the wrong form.
  const stablePropsRef = useRef({ clientId, clientRelationType, clientGuid });
  if (open) {
    stablePropsRef.current = { clientId, clientRelationType, clientGuid };
  }
  const { clientId: stableClientId, clientRelationType: stableRelationType, clientGuid: stableClientGuid } = stablePropsRef.current;

  return (
    <Drawer
      open={open}
      onClose={onClose}
      closeLabel={t(Resources.Common.Close)}
      title={getTitle(t, stableClientId, stableClientGuid, stableRelationType)}
    >
      <Box height="100%">
        {stableClientGuid && stableRelationType === AnalysisClientItemRelationType.Client && (
          <EditClientForm onClose={onClose} clientGuid={stableClientGuid} />
        )}
        {stableRelationType !== AnalysisClientItemRelationType.Client && <FamilyMemberForm clientId={stableClientId} onClose={onClose} />}
      </Box>
    </Drawer>
  );
};
